import { TablePagination } from "../../../components/ui/TablePagination";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useEffect } from "react";
import { Users, CheckCircle2, Clock, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { canSeeRespondents } from "../../../services/pollServices";

/**
 * Screen 9 body — the stat row plus a breakdown matched to Response Type.
 *
 * <p>Targeted / Responses / Pending, then a single-hue bar breakdown. The
 * Responses and Pending tiles are clickable on a non-anonymous poll: they open
 * a modal listing exactly who responded and who hasn't, instead of a separate
 * column on the page.
 *
 * <p>Every bar is one hue: the option label carries identity, so colouring
 * each option differently would encode nothing. Counts wear text tokens.
 */

const BAR = "var(--color-accent)";

function StatTile({ icon: Icon, label, value, tone, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "text-left rounded-xl border border-(--color-text-border)/70 bg-white px-4 py-3.5 w-full",
        onClick && "hover:border-accent/50 hover:shadow-[0_4px_14px_rgba(30,51,131,0.1)] transition cursor-pointer"
      )}>

      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider per-file-hint">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1.5 text-[26px] leading-none font-bold" style={{ color: tone ?? "var(--color-foreground)" }}>
        {value}
      </p>
      {onClick && (
        <p className="mt-1 text-[11px] font-semibold text-accent">
          <Trans i18nKey="formActions.viewList" />
        </p>
      )}
    </Tag>
  );
}

function Bar({ label, count, percent }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-sm text-foreground min-w-0 wrap-break-word">{label}</span>
        <span className="text-[13px] font-semibold per-file-hint whitespace-nowrap shrink-0">
          {count} {count === 1 ? "vote" : "votes"} · {percent}%
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-workspace overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.max(percent, 0)}%`, backgroundColor: BAR, minWidth: percent > 0 ? "4px" : 0 }} />
      </div>
    </div>
  );
}

const PEOPLE_PAGE_SIZE = 10;

/**
 * List modal for Responded / Not-yet-responded. On the Responded list each row
 * also shows what that person answered (`showAnswer`); the pending list is
 * names only. Long lists page rather than scroll — a Global poll can target
 * the whole organisation.
 */
function PeopleModal({ open, title, people, showAnswer, onClose }) {
  const [page, setPage] = useState(0);

  // A fresh open always starts at the first page, and a list that shrinks
  // under the current page (a re-fetch after closing a poll) pulls back.
  const pageCount = Math.max(Math.ceil(people.length / PEOPLE_PAGE_SIZE), 1);
  useEffect(() => {
    if (open) setPage(0);
  }, [open]);
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount - 1));
  }, [pageCount]);

  if (!open) return null;

  const start = page * PEOPLE_PAGE_SIZE;
  const visible = people.slice(start, start + PEOPLE_PAGE_SIZE);

  return (
    <div className="fixed inset-0 z-70 grid place-items-center bg-[#0a1230]/50 backdrop-blur-xs px-4" onClick={onClose}>
      <div
        className="w-full max-w-[460px] max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60 font-sans"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70 shrink-0">
          <h3 className="text-[16px] font-bold text-foreground">{title} ({people.length})</h3>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-workspace"
            aria-label={i18n.t("formActions.close")}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {people.length === 0 ? (
            <p className="text-sm per-file-hint italic">
              <Trans i18nKey="polls.nobodyYet" />
            </p>
          ) : (
            <ul className="space-y-2.5">
              {visible.map((p) => (
                <li key={p.employeeId} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="block text-foreground truncate">{p.name}</span>
                    {p.department && (
                      <span className="block text-[11px] per-file-hint">{p.department}</span>
                    )}
                  </span>
                  {showAnswer && (
                    <span className="text-[13px] font-medium text-accent text-right shrink-0 max-w-[55%] wrap-break-word">
                      {p.answer || "—"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <TablePagination page={page + 1} totalPages={pageCount} totalItems={people.length} pageSize={PEOPLE_PAGE_SIZE} onPageChange={(next) => setPage(next - 1)}  />
      </div>
    </div>
  );
}

/**
 * @param {object} results       aggregation from GET /polls/{id}/results
 * @param {object} participants  respondent lists, or null when not permitted
 */
export function PollResultsView({ poll, viewer, results, participants }) {
  const showLists = canSeeRespondents(poll, viewer) && Boolean(participants);
  const [modal, setModal] = useState(null); // "responded" | "pending" | null

  return (
    <div className="space-y-6">
      {/* Stat row — three tiles, no participation tile and no meter bar. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile icon={Users} label="Targeted" value={results.totalTargeted} />
        <StatTile
          icon={CheckCircle2}
          label="Responses"
          value={results.totalResponses}
          tone="var(--status-published-fg)"
          onClick={showLists ? () => setModal("responded") : undefined} />

        <StatTile
          icon={Clock}
          label="Pending"
          value={results.pending}
          tone="var(--status-warning-fg)"
          onClick={showLists ? () => setModal("pending") : undefined} />
      </div>

      <div className="border-t border-(--color-text-border)/70 pt-6">
        <h3 className="text-sm font-bold text-foreground mb-1">
          <Trans i18nKey="polls.responseBreakdown" />
        </h3>

        {results.kind === "choice" && (
          <>
            <p className="text-[12px] per-file-hint mb-4">
              {poll.responseType === "Multiple Choice"
                ? "Voters could pick more than one option, so percentages are of respondents and may total above 100%."
                : `${results.totalResponses} ${results.totalResponses === 1 ? "respondent" : "respondents"}.`}
            </p>
            {results.totalResponses === 0 ? (
              <p className="text-sm per-file-hint italic">
                <Trans i18nKey="polls.noResponsesYet" />
              </p>
            ) : (
              <div className="space-y-4">
                {results.breakdown.map((row) => (
                  <Bar key={row.key} label={row.label} count={row.count} percent={row.percent} />
                ))}
              </div>
            )}
          </>
        )}

        {results.kind === "rating" && (
          <>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-[34px] leading-none font-bold text-foreground">
                {results.average.toFixed(1)}
              </span>
              <span className="text-sm per-file-hint">
                <Trans i18nKey="polls.averageOf" /> {results.totalResponses}{" "}
                {results.totalResponses === 1 ? "rating" : "ratings"} <Trans i18nKey="polls.scale15" />
              </span>
            </div>
            {results.totalResponses === 0 ? (
              <p className="text-sm per-file-hint italic">
                <Trans i18nKey="polls.noRatingsYet" />
              </p>
            ) : (
              <div className="space-y-4">
                {results.breakdown.map((row) => (
                  <Bar key={row.key} label={`${row.label} ★`} count={row.count} percent={row.percent} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {poll.anonymous && (
        <div className="border-t border-(--color-text-border)/70 pt-6">
          <p className="text-sm per-file-hint">
            <Trans i18nKey="polls.thisIsAnAnonymousPollNoRespondentList" />
          </p>
        </div>
      )}

      {showLists && (
        <>
          <PeopleModal
            open={modal === "responded"}
            title={i18n.t("polls.responded")}
            people={participants.respondents}
            showAnswer
            onClose={() => setModal(null)} />

          <PeopleModal
            open={modal === "pending"}
            title={i18n.t("polls.notYetResponded")}
            people={participants.nonRespondents}
            onClose={() => setModal(null)} />
        </>
      )}
    </div>
  );
}
